import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { useContext } from "react";
import {
    UNSAFE_NavigationContext,
    useLocation,
    UNSAFE_DataRouterContext,
} from "react-router-dom";
import {
    PartialLocation,
    QueryParamAdapter,
    QueryParamAdapterComponent,
} from "use-query-params";

/**
 * Query Param Adapter for react-router v6
 */
export const ReactQueryAdapter: QueryParamAdapterComponent = ({ children }) => {
    // copied from https://raw.githubusercontent.com/pbeshai/use-query-params/refs/heads/master/packages/use-query-params-adapter-react-router-6/src/index.ts
    // need to use state aware navigate to avoid stale state
    const { navigator } = useContext(UNSAFE_NavigationContext);
    const navigate = useStateAwareNavigate();
    const { organizationId, workspaceId } = useScope();
    const router = useContext(UNSAFE_DataRouterContext)?.router;
    const location = useLocation();

    // The freshest current location. Order matters: during a route transition
    // `useLocation()` is a render-stale snapshot, while `router.state.location`
    // / the history `navigator.location` are updated synchronously on navigate.
    const getCurrentLocation = () =>
        router?.state?.location ?? (navigator as any)?.location ?? location;

    // Navigate to an explicit { pathname, search } rather than a search-only
    // relative target. A search-only navigate resolves relative to the pathname
    // React Router currently considers active, which is stale while a route
    // transition is mid-flight: a query-param write flushed just after a
    // cross-route navigation (e.g. the General Ledger resolving its ledgerId on
    // mount, right after drilling in from a consolidated report) would land the
    // merged query back on the previous route and clobber the navigation.
    // Pinning the pathname to the fresh current location keeps the write on the
    // destination route.
    const adapter: QueryParamAdapter = {
        replace(l: PartialLocation) {
            navigate(
                {
                    pathname: getCurrentLocation().pathname,
                    search: l.search ?? "",
                },
                {
                    replace: true,
                    state: {
                        ...l.state,
                        organizationId,
                        workspaceId,
                    },
                },
            );
        },
        push(l: PartialLocation) {
            navigate(
                {
                    pathname: getCurrentLocation().pathname,
                    search: l.search ?? "",
                },
                {
                    replace: false,
                    state: {
                        ...l.state,
                        organizationId,
                        workspaceId,
                    },
                },
            );
        },
        get location() {
            // be a bit defensive here in case of an unexpected breaking change in React Router
            return getCurrentLocation();
        },
    };

    return children(adapter);
};
